// ============================================================
// ClientDirectory.tsx
// ★ The Clients search screen — "People directory" (approved Option B,
// casevo-clients-B-people.html). Every size / colour / radius / shadow /
// letter-spacing is the mockup's CSS copied as a Tailwind arbitrary value,
// with the mockup's dark value as an explicit dark: variant. Mockup-only
// tones are literals: hair #e1ece6/#34463b, redbg/red for the error state.
//
// Presentational: ClientSearchView owns the search state, the debounced
// load, the store slice, the new-clients counter and the click-through
// (pushViewHistory + handleSelectClient) — this renders them.
//   • search box   — name / NI number / phone / postcode / client number
//                    (the backend's OR-matchers), min 2 chars, clear button
//   • hints row    — the searchable fields; the ones the current results
//                    actually matched on are highlighted
//   • new-clients  — "N new clients added since this search ran — Refresh"
//   • states       — before-typing / searching / error / no matches
//   • results      — A–Z by surname, 2-up avatar-led cards (1-up on mobile):
//                    initials, name + client number, contact + ID block,
//                    and a case footer band (ref + status chips, from the
//                    GET /api/clients?search case summary)
// ============================================================
import { LOCKED_CASE_TIP, LockIcon } from '../shared/CaseLink'
import { RESTRICTED_CLIENT_TIP } from '../../api/casevoApi'
import React from 'react'
import type { Client, ClientCaseSummary } from '../../types/client.types'
import { caseName } from '../../utils/caseName'

// ── Icons (Feather line icons, currentColor — design doc §6) ─────────
const P: Record<string, React.ReactNode> = {
  search: <><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>,
  x: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
  user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  id: <><rect x="2" y="5" width="20" height="14" rx="2" /><circle cx="8" cy="12" r="2.5" /><line x1="13" y1="10" x2="18" y2="10" /><line x1="13" y1="14" x2="17" y2="14" /></>,
  phone: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />,
  pin: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></>,
  hash: <><line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" /><line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" /></>,
  mail: <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></>,
  chevr: <polyline points="9 18 15 12 9 6" />,
  refresh: <><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></>,
  lock: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
  alert: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>,
}
const Ic: React.FC<{ n: keyof typeof P; className?: string; strokeWidth?: number }> = ({ n, className = 'w-[14px] h-[14px]', strokeWidth = 2 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    className={`shrink-0 ${className}`} aria-hidden="true">{P[n]}</svg>
)

// ── Shared recipes (mockup → Tailwind) ─────────────────────────────────
// .card / .pc
const CARD = 'bg-casevo-surface border border-casevo-border rounded-[14px] shadow-[0_1px_2px_rgba(16,42,29,0.04),0_6px_18px_rgba(16,42,29,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.25),0_4px_14px_rgba(0,0,0,0.18)]'
const HAIR = 'border-casevo-border-soft'
// mark — the matched search term (gold tint: a highlighter, not structure)
const MARK = 'bg-[rgba(224,182,74,0.28)] dark:bg-[rgba(224,182,74,0.26)] text-inherit rounded-[3px] px-[2px]'
// .pav a–e — soft identity tints (light / dark)
const TINTS = [
  'bg-[#d8f3e8] text-[#1a3a2a] dark:bg-[#2f5a45] dark:text-[#d8f3e8]',
  'bg-[#dfe7f7] text-[#23385f] dark:bg-[#34466a] dark:text-[#dfe7f7]',
  'bg-[#f5e3d8] text-[#6b3a1c] dark:bg-[#5e4332] dark:text-[#f5e3d8]',
  'bg-[#e9e0f5] text-[#43306b] dark:bg-[#4c3f66] dark:text-[#ece4f8]',
  'bg-[#e7efd6] text-[#3d4d16] dark:bg-[#4a5530] dark:text-[#eef4dc]',
]

export const clientDisplayName = (c: Client): string =>
  [c.title, c.forename, c.surname].filter(Boolean).join(' ') || 'Unnamed Client'
const initials = (c: Client): string =>
  ((c.forename || '').charAt(0) + (c.surname || '').charAt(0)).toUpperCase() || '?'
// Stable tint per client (so a client keeps their colour across searches).
const tintFor = (c: Client): string => {
  let h = 0
  for (const ch of c.id) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  return TINTS[h % TINTS.length]
}

// Highlight every case-insensitive occurrence of the query in a string.
const Hl: React.FC<{ text: string; q: string }> = ({ text, q }) => {
  if (!q || !text) return <>{text}</>
  const lower = text.toLowerCase()
  const needle = q.toLowerCase()
  const out: React.ReactNode[] = []
  let i = 0
  let k = 0
  for (;;) {
    const at = lower.indexOf(needle, i)
    if (at < 0) break
    if (at > i) out.push(text.slice(i, at))
    out.push(<mark key={k++} className={MARK}>{text.slice(at, at + needle.length)}</mark>)
    i = at + needle.length
  }
  out.push(text.slice(i))
  return <>{out}</>
}

// Which searchable fields the results matched on (drives the hints row) —
// the SAME fields the backend's OR-matchers use (clientController getClients).
type Field = 'name' | 'ni' | 'phone' | 'postcode' | 'number'
const matchedFields = (clients: Client[], q: string): Set<Field> => {
  const s = new Set<Field>()
  const n = q.trim().toLowerCase()
  if (!n) return s
  for (const c of clients) {
    if (c.restricted) continue // (Audit batch 3) a bare stub — nothing to match
    // Full name, any word order (mirrors the backend's multi-word matcher).
    const nm = `${c.forename} ${c.surname}`.toLowerCase()
    if (nm.includes(n) || n.split(/[\s,]+/).filter(Boolean).every((w) => nm.includes(w))) s.add('name')
    if ((c.niNumber || '').toLowerCase().includes(n)) s.add('ni')
    if ((c.mainContactNumber || '').toLowerCase().includes(n)) s.add('phone')
    if ((c.addressPostcode || '').toLowerCase().includes(n)) s.add('postcode')
    if (String(c.clientNumber) === n) s.add('number')
  }
  return s
}

// ── Case chip (.kc) ─────────────────────────────────────────────────────
const STATUS_CLS: Record<ClientCaseSummary['status'], string> = {
  open: 'bg-[#e3f2ea] text-[#2d6a4f] dark:bg-[rgba(127,209,168,0.14)] dark:text-[#7fd1a8]',
  draft: 'bg-casevo-input text-casevo-text-soft border border-casevo-border',
  closed: 'bg-casevo-input text-casevo-text-soft border border-casevo-border opacity-70',
}
const CaseChip: React.FC<{ k: ClientCaseSummary; client: Pick<Client, 'forename' | 'surname'>; onOpen?: (caseId: string) => void; opening?: boolean }> = ({ k, client, onOpen, opening = false }) => {
  // THE case name (shared caseName() rule) — the same name the case card and
  // quick view show. The card's own client fills in if the snapshot is blank.
  const clientName = `${client.forename} ${client.surname}`.trim()
  const name = caseName({ caseName: k.caseName, clientForename: k.clientForename, clientSurname: k.clientSurname, defendantName: k.defendantName, client })
  // Shown only when it adds something (not just the client's own name, which
  // the card already leads with) and the viewer may open the case.
  const showName = k.accessible && name.toLowerCase() !== clientName.toLowerCase()
  const inner = (
    <>
      {!k.accessible && <LockIcon className="w-[11px] h-[11px] text-casevo-text-soft" />}
      <span className={`font-mono text-[11px] tracking-[0.05em] ${k.accessible ? 'text-casevo-brand' : 'text-casevo-text-soft'}`}>{k.ref}</span>
      {showName && <span className="truncate">{name}</span>}
      <span className={`text-[9.5px] tracking-[0.14em] font-bold px-[5px] py-[1px] rounded-[3px] uppercase shrink-0 ${STATUS_CLS[k.status]}`}>{k.status}</span>
      {opening && <span className="w-[10px] h-[10px] rounded-full border-2 border-casevo-mint border-t-casevo-brand animate-spin motion-reduce:animate-none shrink-0" aria-hidden="true" />}
    </>
  )
  const base = 'inline-flex items-center gap-[7px] bg-casevo-surface border border-casevo-border rounded-[7px] px-[8px] py-[4px] text-[12px] text-casevo-forest max-w-full min-w-0'
  // ★ LOCKED — the viewer can't open this case: greyed, lock icon, no action.
  // Clicks are swallowed (stopPropagation) so a click on a locked chip
  // doesn't fall through and open the client either.
  if (!k.accessible || !onOpen) {
    return (
      // Same locked pattern as the shared CaseLink (role="link" +
      // aria-disabled + LOCKED_CASE_TIP + sr-only reason) — the old
      // role="img" hid the chip's text (ref/name/status) from screen readers.
      <span role={k.accessible ? undefined : 'link'} aria-disabled={!k.accessible || undefined}
        title={k.accessible ? undefined : LOCKED_CASE_TIP}
        onClick={(e) => e.stopPropagation()}
        className={`${base} ${k.accessible ? '' : 'opacity-70 cursor-not-allowed select-none'}`}>
        {inner}
        {!k.accessible && <span className="sr-only">({LOCKED_CASE_TIP})</span>}
      </span>
    )
  }
  // ★ ACCESSIBLE — opens the case quick-view modal. Its own button (the card
  // is a non-button container), stopPropagation so the card's client
  // click-through doesn't also fire.
  return (
    <button type="button" aria-label={`Open case ${k.ref}${showName ? ` — ${name}` : ''} in quick view`} title="Open case"
      disabled={opening}
      onClick={(e) => { e.stopPropagation(); onOpen(k.id) }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation() }}
      className={`${base} cursor-pointer transition-colors hover:border-casevo-mid hover:bg-casevo-mint/40 dark:hover:bg-casevo-mint/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid disabled:cursor-wait`}>
      {inner}
    </button>
  )
}

// ── (Audit batch 3, H4) A client behind an ethical wall ─────────────────
// The search returned only a stub (id + "Restricted client"): the same
// show-but-lock treatment as a restricted case card — dashed, lock icon, no
// name / number / contact, and opening it shows the "Restricted client" page.
const RestrictedClientCard: React.FC<{ onOpen: () => void }> = ({ onOpen }) => (
  <button type="button" onClick={onOpen} aria-label="Restricted client — you don’t have access"
    title={RESTRICTED_CLIENT_TIP}
    className="min-h-[142px] flex flex-col items-start gap-2 pt-[18px] pb-[16px] px-[18px] rounded-[14px] border border-dashed border-casevo-border bg-transparent text-casevo-text-soft text-left cursor-pointer transition-colors hover:border-casevo-mid focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid">
    <span className="w-[34px] h-[34px] rounded-[10px] bg-casevo-border-soft grid place-items-center shrink-0"><Ic n="lock" className="w-[16px] h-[16px]" /></span>
    <span>
      <span className="block font-semibold text-[14px] text-casevo-forest">Restricted client</span>
      <span className="block text-[12.5px]">{RESTRICTED_CLIENT_TIP}</span>
    </span>
  </button>
)

// ── Client card (.pc) ───────────────────────────────────────────────────
const ClientCard: React.FC<{ c: Client; q: string; onOpen: () => void; onOpenCase?: (caseId: string) => void; openingCaseId?: string | null }> = ({ c, q, onOpen, onOpenCase, openingCaseId }) => {
  const name = clientDisplayName(c)
  const address = [c.addressLine1, c.addressPostcode].filter(Boolean).join(', ')
  const cases = c.caseSummaries
  return (
    // A focusable role=button container (not a <button>) so the case chips
    // inside can be real buttons (nested buttons are invalid HTML). Enter /
    // Space open the client only when the card itself has focus.
    <div role="button" tabIndex={0} onClick={onOpen} aria-label={`Open client ${name}`}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); onOpen() } }}
      className={`${CARD} group overflow-hidden flex flex-col text-left cursor-pointer transition-[border-color,box-shadow] duration-150 hover:border-casevo-mid hover:shadow-[0_10px_26px_rgba(16,42,29,0.12)] dark:hover:shadow-[0_10px_26px_rgba(0,0,0,0.35)] focus:outline-none focus-visible:border-casevo-mid focus-visible:ring-2 focus-visible:ring-casevo-mid`}>
      {/* .hd */}
      <div className="flex items-center gap-[14px] pt-[18px] px-[18px] pb-[14px] w-full">
        <span className={`w-[52px] h-[52px] rounded-full flex items-center justify-center font-bold text-[17px] tracking-[0.02em] shrink-0 ${tintFor(c)}`}>{initials(c)}</span>
        <span className="min-w-0">
          <span className="block text-[17px] font-semibold text-casevo-forest leading-[1.2] truncate"><Hl text={name} q={q} /></span>
          <span className="inline-flex items-center gap-[5px] mt-[4px] text-[11px] leading-[15px] tracking-[0.14em] uppercase text-casevo-text-soft font-semibold">
            Client<b className="font-mono leading-[15px] tracking-[0.06em] text-casevo-brand font-medium"><Hl text={String(c.clientNumber)} q={q} /></b>
          </span>
        </span>
        <span className="ml-auto inline-flex items-center gap-[5px] text-[11.5px] tracking-[0.1em] uppercase font-semibold text-casevo-brand shrink-0">
          Open<Ic n="chevr" />
        </span>
      </div>
      {/* .rows — contact + ID block */}
      <div className="px-[18px] pb-[14px] grid grid-cols-1 min-[480px]:grid-cols-2 gap-x-[16px] gap-y-[8px] w-full">
        <Row icon="phone" muted={!c.mainContactNumber}>{c.mainContactNumber ? <Hl text={c.mainContactNumber} q={q} /> : 'No phone'}</Row>
        <Row icon="mail" muted={!c.emailAddress}>{c.emailAddress || 'No email'}</Row>
        <Row icon="pin" muted={!address}>{address ? <><Hl text={c.addressLine1} q={q} />{c.addressLine1 && c.addressPostcode ? ', ' : ''}<Hl text={c.addressPostcode} q={q} /></> : 'No address'}</Row>
        <Row icon="id" muted={!c.niNumber} mono={!!c.niNumber}>{c.niNumber ? <Hl text={c.niNumber} q={q} /> : 'No NI number'}</Row>
      </div>
      {/* .ft — case footer band */}
      <div className={`mt-auto w-full border-t ${HAIR} bg-casevo-input px-[18px] py-[11px] flex items-center gap-[8px] flex-wrap`}>
        <span className="text-[10.5px] tracking-[0.16em] uppercase font-bold text-casevo-text-soft mr-[4px]">Cases</span>
        {cases === undefined ? (
          <span className="text-[12.5px] text-casevo-text-soft italic">—</span>
        ) : cases.length === 0 ? (
          <span className="text-[12.5px] text-casevo-text-soft italic">No cases yet</span>
        ) : (
          cases.map((k) => <CaseChip key={k.id} k={k} client={c} onOpen={onOpenCase} opening={openingCaseId === k.id} />)
        )}
      </div>
    </div>
  )
}
const Row: React.FC<{ icon: keyof typeof P; muted?: boolean; mono?: boolean; children: React.ReactNode }> = ({ icon, muted = false, mono = false, children }) => (
  <span className={`flex items-center gap-[8px] text-[13px] min-w-0 ${muted ? 'text-casevo-text-soft italic' : 'text-casevo-forest'}`}>
    <Ic n={icon} className="w-[14px] h-[14px] text-casevo-mid" />
    <span className={`whitespace-nowrap overflow-hidden text-ellipsis ${mono ? 'font-mono text-[12px] leading-[16px]' : ''}`}>{children}</span>
  </span>
)

// ── Page ────────────────────────────────────────────────────────────────
export interface ClientDirectoryProps {
  search: string
  onSearch: (v: string) => void
  inputRef?: React.RefObject<HTMLInputElement | null>
  minChars: number
  clients: Client[]            // already sorted A–Z by surname
  loading: boolean
  error: string | null
  newCount: number
  onRefresh: () => void
  onOpen: (c: Client) => void
  /** Case chip → the case quick-view modal (accessible cases only). */
  onOpenCase?: (caseId: string) => void
  /** The chip whose case is being fetched for the quick view (spinner). */
  openingCaseId?: string | null
}

const HINTS: { key: Field; label: string; icon: keyof typeof P }[] = [
  { key: 'name', label: 'Name', icon: 'user' },
  { key: 'ni', label: 'NI number', icon: 'id' },
  { key: 'phone', label: 'Phone', icon: 'phone' },
  { key: 'postcode', label: 'Postcode', icon: 'pin' },
  { key: 'number', label: 'Client no.', icon: 'hash' },
]

const ClientDirectory: React.FC<ClientDirectoryProps> = (p) => {
  const q = p.search.trim()
  const active = q.length >= p.minChars
  const showResults = active && !p.loading && !p.error && p.clients.length > 0
  const matched = showResults ? matchedFields(p.clients, q) : new Set<Field>()

  return (
    // leading-[normal]: the mockup has no preflight (line-height: normal).
    <div className="font-sans leading-[normal]">
      {/* ── PAGE HEAD (.phead) ── */}
      <div className="mb-[22px]">
        <div className="text-[12px] tracking-[0.18em] uppercase text-casevo-brand mb-[6px]">Client Search</div>
        <h1 className="text-[22px] sm:text-[26px] font-medium tracking-[-0.01em] text-casevo-forest leading-[1.15]">Find a client</h1>
        <div className="text-[13.5px] text-casevo-text-soft mt-[5px]">Search the firm's clients — open one to see their details, cases, tasks, notes and activity.</div>
      </div>

      {/* ── SEARCH CARD (.searchcard) ── */}
      <div className={`${CARD} pt-[18px] px-[18px] pb-[14px] mb-[18px]`}>
        <label className="group/sb flex items-center gap-[12px] h-[52px] border-[1.5px] border-casevo-border rounded-[10px] px-[14px] bg-casevo-surface transition-[border-color,box-shadow] focus-within:border-casevo-brand focus-within:shadow-[0_0_0_4px_rgba(45,106,79,0.10)] dark:focus-within:shadow-[0_0_0_4px_rgba(82,168,127,0.14)]">
          <Ic n="search" className="w-[18px] h-[18px] text-casevo-brand" />
          <input ref={p.inputRef} type="text" value={p.search} onChange={(e) => p.onSearch(e.target.value)}
            placeholder="Search by name, NI number, phone, postcode, or client number…" autoComplete="off" aria-label="Search clients"
            className="flex-1 min-w-0 bg-transparent outline-none text-[17px] text-casevo-forest placeholder:text-casevo-text-soft placeholder:text-[15px]" />
          {p.search.length > 0 && (
            <button type="button" aria-label="Clear search" onClick={() => { p.onSearch(''); p.inputRef?.current?.focus() }}
              className="w-[26px] h-[26px] rounded-full bg-casevo-input text-casevo-text-soft flex items-center justify-center hover:text-casevo-forest focus:outline-none focus-visible:ring-2 focus-visible:ring-casevo-mid">
              <Ic n="x" className="w-[13px] h-[13px]" />
            </button>
          )}
        </label>
        <div className="flex items-center gap-[8px] flex-wrap mt-[12px] text-[12px] text-casevo-text-soft">
          <span className="tracking-[0.14em] uppercase text-[10.5px] font-semibold mr-[2px]">Searches</span>
          {HINTS.map((h) => {
            const on = matched.has(h.key)
            return (
              <span key={h.key} className={`inline-flex items-center gap-[5px] px-[9px] py-[3px] rounded-full text-[12px] ${on ? 'bg-casevo-mint text-casevo-brand dark:text-[#7fd1a8] font-semibold' : 'bg-casevo-input text-casevo-mid'}`}>
                <Ic n={h.icon} className="w-[12px] h-[12px]" />{h.label}
              </span>
            )
          })}
        </div>
      </div>

      {/* ── NEW-CLIENTS BANNER (.newbar) — only with an active search ── */}
      {active && p.newCount > 0 && (
        <div className="flex items-center gap-[10px] bg-casevo-mint text-casevo-brand rounded-[10px] px-[14px] py-[9px] text-[13px] mb-[18px]">
          <Ic n="refresh" />
          <span><b className="font-semibold text-casevo-forest">{p.newCount} new client{p.newCount === 1 ? '' : 's'}</b> added since this search ran</span>
          <button type="button" onClick={p.onRefresh} className="ml-auto font-semibold underline underline-offset-[3px] focus:outline-none casevo-focus">Refresh</button>
        </div>
      )}

      {/* ── STATES / RESULTS ── */}
      {!active ? (
        <Empty icon="search" title="Search for a client" body="Type a name, NI number, phone number, postcode, or client number to find a client." />
      ) : p.error ? (
        <div className="flex items-center gap-[10px] px-[16px] py-[12px] rounded-[14px] border text-[13.5px] bg-[#fbeae7] border-[#f1c9c2] text-[#c23b2c] dark:bg-[rgba(243,160,148,0.14)] dark:border-[rgba(243,160,148,0.28)] dark:text-[#f3a094]">
          <Ic n="alert" />{p.error}
        </div>
      ) : p.loading && p.clients.length === 0 ? (
        <>
          <ResultsHeading label="Searching" q={q} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px]" aria-busy="true">
            {[0, 1].map((i) => (
              <div key={i} className={`${CARD} overflow-hidden animate-pulse`}>
                <div className="flex items-center gap-[14px] pt-[18px] px-[18px] pb-[14px]">
                  <span className="w-[52px] h-[52px] rounded-full bg-casevo-mint/70" />
                  <span className="flex-1"><span className="block w-40 h-[15px] rounded bg-casevo-mint/60 mb-[8px]" /><span className="block w-24 h-[10px] rounded bg-casevo-mint/50" /></span>
                </div>
                <div className="px-[18px] pb-[14px] grid grid-cols-2 gap-[10px]">
                  {[0, 1, 2, 3].map((j) => <span key={j} className="h-[12px] rounded bg-casevo-mint/40" />)}
                </div>
                <div className={`border-t ${HAIR} bg-casevo-input h-[46px]`} />
              </div>
            ))}
          </div>
        </>
      ) : p.clients.length === 0 ? (
        <Empty icon="search" title={`No clients matched “${q}”`} body="Check the spelling, or try an NI number, phone number, postcode or client number." />
      ) : (
        <>
          <ResultsHeading label="Results" count={p.clients.length} q={q} busy={p.loading} />
          {/* .grid — 2-up, collapsing to 1-up below md */}
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-[16px] transition-opacity ${p.loading ? 'opacity-[0.6]' : ''}`}>
            {p.clients.map((c) => (c.restricted
              ? <RestrictedClientCard key={c.id} onOpen={() => p.onOpen(c)} />
              : <ClientCard key={c.id} c={c} q={q} onOpen={() => p.onOpen(c)} onOpenCase={p.onOpenCase} openingCaseId={p.openingCaseId} />))}
          </div>
        </>
      )}
    </div>
  )
}

// .gh — results heading
const ResultsHeading: React.FC<{ label: string; count?: number; q: string; busy?: boolean }> = ({ label, count, q, busy }) => (
  <div className="flex items-center gap-[9px] text-[12px] font-bold tracking-[0.16em] uppercase text-[#6f7d73] dark:text-[#8aa899] mx-[2px] mb-[12px]">
    {label}
    {count !== undefined && <span className="font-semibold tracking-[0.04em] text-casevo-text-soft">{count}</span>}
    <span className="text-[12.5px] tracking-[0.04em] normal-case font-medium text-casevo-text-soft">for “{q}” · A–Z by surname</span>
    {busy && <span className="w-[12px] h-[12px] rounded-full border-2 border-casevo-mint border-t-casevo-brand animate-spin motion-reduce:animate-none" aria-label="Updating" />}
    <span className="flex-1 h-px bg-casevo-border-soft" />
  </div>
)

// .empty — before-typing / no-matches card
const Empty: React.FC<{ icon: keyof typeof P; title: string; body: string }> = ({ icon, title, body }) => (
  <div className={`${CARD} py-[44px] px-[24px] text-center`}>
    <div className="w-[64px] h-[64px] rounded-full bg-casevo-mint text-casevo-brand flex items-center justify-center mx-auto mb-[14px]">
      <Ic n={icon} className="w-[26px] h-[26px]" />
    </div>
    <h3 className="text-[19px] font-semibold text-casevo-forest mb-[6px]">{title}</h3>
    <p className="text-[13.5px] text-casevo-text-soft">{body}</p>
  </div>
)

export default ClientDirectory
